import { useEffect, useRef, useState } from 'react';
import { debounce, type DebouncedFunc } from 'lodash';

import { usePrerenderUser } from '../../../hooks/usePrerenderUser';
import { useEvent } from '../../events/hooks/useEvent';
import { SETTINGS_UPDATED_FLAGS, type SettingsUpdatedFlags } from '../../events/settingsUpdatedEvent';
import { useUpdateSettingMutation, type SettingsUpdate } from '../api/settingsApiSlice';
import type { SaveStatus } from '../components/SaveIndicator';

// Only these fields map to a 'Settings Updated' flag (matching the legacy Apply-button analytics,
// where recaching_status_is_updated meant the rendering-DEVICE on/off status, not recache-pause);
// every other saved field reports all flags false. token_is_updated is owned by the
// token-regeneration flow (RegenerateToken.jsx), never by an auto-save.
const FLAG_BY_FIELD: Partial<Record<keyof SettingsUpdate, keyof SettingsUpdatedFlags>> = {
  pageLoadTimeout: 'rendering_timeout_is_updated',
  renderRegion: 'rendering_location_is_updated',
  forceHTTPS: 'https_status_is_updated',
  mobileAdaptive: 'recaching_status_is_updated',
};

function settingsUpdatedFlags(partial: SettingsUpdate): SettingsUpdatedFlags {
  const flags: SettingsUpdatedFlags = { ...SETTINGS_UPDATED_FLAGS };
  (Object.keys(partial) as (keyof SettingsUpdate)[]).forEach((field) => {
    const flag = FLAG_BY_FIELD[field];
    if (flag) flags[flag] = true;
  });
  return flags;
}

// Two payloads target the same setting when they carry the same field names — the values differ
// (that is the point of re-saving). Comparing by object identity instead would miss a fresh save
// of the very field that failed, leaving a stale error the user can never clear.
function targetsSameFields(one: SettingsUpdate, other: SettingsUpdate): boolean {
  const oneFields = Object.keys(one).sort();
  const otherFields = Object.keys(other).sort();
  return oneFields.length === otherFields.length && oneFields.every((field, index) => field === otherFields[index]);
}

type AutoSaveSetting = {
  save: (partial: SettingsUpdate) => Promise<void>;
  saveDebounced: (partial: SettingsUpdate, delayMs: number) => void;
  cancelPending: () => void;
  status: SaveStatus;
  retry: () => Promise<void>;
};

export function useAutoSaveSetting(): AutoSaveSetting {
  const { id: userId } = usePrerenderUser();
  const { track } = useEvent();
  const [updateSetting] = useUpdateSettingMutation();
  const [status, setStatus] = useState<SaveStatus>('idle');
  const lastPartial = useRef<SettingsUpdate | null>(null);
  // The payload of the most recent save that failed, kept so retry() resends *that* change rather
  // than whatever was saved last. Cleared only when a save of the same field(s) later succeeds, so
  // a newer unrelated save succeeding in between neither loses the thing the user still needs to
  // retry nor hides the error that offers the retry.
  const failedPartial = useRef<SettingsUpdate | null>(null);

  // Saves are serialized through this promise chain: at most one PUT per hook instance is in
  // flight, and they land in the order they were enqueued.
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const lastEnqueuedSaveId = useRef(0);
  const saveIdSequence = useRef(0);

  const runSave = async (partial: SettingsUpdate, saveId: number): Promise<void> => {
    // Only the most recently enqueued save owns `status`; an older save settling late must not
    // overwrite a newer save's outcome.
    const isLatest = (): boolean => saveId === lastEnqueuedSaveId.current;

    lastPartial.current = partial;
    if (isLatest()) setStatus('saving');
    try {
      await updateSetting({ userId, ...partial }).unwrap();
      if (failedPartial.current && targetsSameFields(failedPartial.current, partial)) failedPartial.current = null;
      // An unrelated save succeeding must not report overall success while an earlier field is
      // still unsaved — the user would lose both the error and the retry that recovers it.
      if (isLatest()) setStatus(failedPartial.current ? 'error' : 'success');
      track('Settings Updated', settingsUpdatedFlags(partial));
    } catch (error) {
      console.error('Failed to auto-save a setting', { partial, error });
      failedPartial.current = partial;
      if (isLatest()) setStatus('error');
    }
  };

  // Route saves through a ref so a save scheduled on one render still uses the latest closure
  // (userId/track/updateSetting) when its timer eventually fires.
  const runSaveRef = useRef(runSave);
  runSaveRef.current = runSave;

  // Relies on runSave never rejecting (it reports failures via `status`): a rejection here would
  // poison the chain and wedge every later save on this instance.
  const enqueueSave = (partial: SettingsUpdate): Promise<void> => {
    saveIdSequence.current += 1;
    const saveId = saveIdSequence.current;
    lastEnqueuedSaveId.current = saveId;
    const queued = saveChain.current.then(() => runSaveRef.current(partial, saveId));
    saveChain.current = queued;
    return queued;
  };

  // At most one debounced save is pending per hook instance. A new saveDebounced() cancels the
  // pending one (the newer value replaces it); an explicit save() or unmount flushes it into the
  // chain first, so the earlier change still lands, and lands first.
  const pendingSave = useRef<DebouncedFunc<(partial: SettingsUpdate) => void> | null>(null);

  const flushPending = (): void => {
    pendingSave.current?.flush();
  };

  const cancelPending = (): void => {
    pendingSave.current?.cancel();
    pendingSave.current = null;
  };

  const save = async (partial: SettingsUpdate): Promise<void> => {
    flushPending();
    await enqueueSave(partial);
  };

  const saveDebounced = (partial: SettingsUpdate, delayMs: number): void => {
    pendingSave.current?.cancel();
    pendingSave.current = debounce((scheduled: SettingsUpdate) => {
      void enqueueSave(scheduled);
    }, delayMs);
    pendingSave.current(partial);
  };

  // Flush any still-pending debounced save on unmount so a user who edits and navigates away
  // before the debounce elapses doesn't silently lose the change.
  useEffect(() => () => flushPending(), []);

  const retry = async (): Promise<void> => {
    const partial = failedPartial.current ?? lastPartial.current;
    if (!partial) return;
    await enqueueSave(partial);
  };

  return { save, saveDebounced, cancelPending, status, retry };
}
